import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import Login from "../modules/login";
import Register from "../modules/register";
import Home from "../modules/home";
import NewPrescription from "../modules/prescriptions/new";
import NewOrder from "../modules/orders/new";
import TermsAndConditions from "../modules/termsAndConditions";
import { AuthProvider, useAuth } from "../auth/context";
import RequestPasswordRecovery from "../modules/passwordRecovery";
import ChangePassword from "../modules/passwordRecovery/change";
import ResponsiveAppBar from "./ResponsiveAppBar";
import { Stack } from "@mui/material";
import Prescriptions from "../modules/prescriptions";
import NewPatient from "../modules/patients/new";
import Settings from "../modules/settings";
import Trials from "../modules/trials";
import PatientSelfRegister from "../modules/patientSelfRegister";
import { useAutoSignout } from "../utils/hooks";
import DetailsDocument from "../modules/detailsDocument";
import Templates from "../modules/templates";
import Patients from "../modules/patients";
import PatientDetail from "../modules/patients/detail";
import ShareRegistrationDialog from "../modules/utils/patient/new/ShareRegistrationDialog";
import DownloadMedicalDocuments from "../modules/medicalDocuments/download";
import Calculators from "../modules/calculators";

const WithAuthProvider = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

const ProtectedRoute = () => {
  useAutoSignout();

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "row",
        pt: { xs: 5, md: 0 },
        height: "100%",
        boxSizing: { xs: "border-box" }
      }}
    >
      <ResponsiveAppBar />
      <Outlet />
    </Stack>
  );
};

const DoctorRoute = ({ children }) => {
  const { user } = useAuth();

  if (user.type != "doctor")
    return <Navigate to={user.landingRoute()} />;

  return children;
};

export default createBrowserRouter([
  {
    element: <WithAuthProvider />,
    children: [
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "register",
        element: <Register />,
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/",
            element: (
              <DoctorRoute>
                <Home />
              </DoctorRoute>
            ),
          },
          {
            path: "/settings",
            element: (
              <DoctorRoute>
                <Settings />
              </DoctorRoute>
            ),
          },
          {
            path: "prescriptions",
            children: [
              {
                path: "",
                element: <Prescriptions />,
              },
              {
                path: "new",
                element: (
                  <DoctorRoute>
                    <NewPrescription />
                  </DoctorRoute>
                ),
              },
            ],
          },
          {
            path: "/templates",
            element: (
              <DoctorRoute>
                <Templates />
              </DoctorRoute>
            ),
          },
          {
            path: "orders",
            children: [
              {
                path: "new",
                element: (
                  <DoctorRoute>
                    <NewOrder />
                  </DoctorRoute>
                ),
              },
            ],
          },
          {
            path: "calculators",
            element: (
              <DoctorRoute>
                <Calculators />
              </DoctorRoute>
            ),
          },
          {
            path: "details-document",
            children: [
              {
                path: ":id",
                element: <DetailsDocument />,
              },
            ],
          },
          {
            path: "patients",
            children: [
              {
                path: "",
                element: <Patients />,
              },

              {
                path: ":id",
                element: <PatientDetail />,
              },

              {
                path: "new",
                element: <NewPatient />,
              },
              {
                path: "new/link-self-registration",
                element: <ShareRegistrationDialog />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "termsAndConditions",
    element: <TermsAndConditions />,
  },
  {
    path: "passwordRecovery",
    children: [
      {
        path: "request",
        element: <RequestPasswordRecovery />,
      },
      {
        path: "change",
        element: <ChangePassword />,
      },
    ],
  },
  {
    path: "patients/selfRegister",
    element: <PatientSelfRegister />,
  },
  {
    path: "medical-documents/download",
    element: <DownloadMedicalDocuments />,
  },
]);
